import React, { useState } from 'react';
import { IonIcon } from '@ionic/react';
import { close, checkmarkCircle, heart, play } from 'ionicons/icons';
import { ReactSVG } from 'react-svg';
import { SortByPopup } from './SortByPopup';
import { useAppSelector } from 'appRedux/createStore';
import { useDispatch, useSelector } from 'react-redux';
import { SlotCompareReduxCommandCreator } from 'appRedux/actions/slotCompareCommandCreator';
import { featureFlags } from 'config/featureFlags';
import useFavorites from 'common/reactHooks/useFavorites';
import usePlaylist, { isIncludedInPlaylist } from 'common/reactHooks/usePlaylist';
import { selectCasinoId, selectUserId, selectFavoritesList, selectPlaylist } from 'appRedux/selectors';
import { isIncludedInFavorites } from 'pages/Favorites/components/LikeButton';

export const INSIGHTS = [
  { label: 'None', value: 'none' },
  { label: 'Spins', value: 'spins' },
  { label: 'Coin-in', value: 'coinIn' },
  { label: 'Coin-out', value: 'coinOut' },
  { label: '$ Wins/Spin', value: 'winsPerSpin' },
];

export const RANGES = [
  { label: 'Time frame', value: 'timeFrame', disabled: false },
  { label: 'Spins', value: 'spins', disabled: false },
  { label: 'Coin-in', value: 'coinIn', disabled: true },
];

interface ComparisonViewHeaderProps {
  onAddToFavorites?: () => void;
  onAddToPlaylist?: () => void;
}

const ComparisonViewHeader: React.FC<ComparisonViewHeaderProps> = ({
  onAddToFavorites,
  onAddToPlaylist,
}) => {
  const [showFilterPopover, setShowFilterPopover] = useState(false);
  const dispatch = useDispatch();
  const slotCompareCommands = SlotCompareReduxCommandCreator(dispatch);
  const { selectedSlots } = useAppSelector(
    (state) => state.app.persistedState.compareSlotsState
  );
  const userId = useSelector(selectUserId);
  const casinoId = useSelector(selectCasinoId);
  const favoritesList = useSelector(selectFavoritesList);
  const playlist = useSelector(selectPlaylist);
  const { handleInsertFavoriteBulk } = useFavorites({});
  const { handleInsertPlaylistBulk } = usePlaylist();
  const isPlaylistEnabled = featureFlags.playlist;

  const onBackAction = () => {
    if (!featureFlags.drawerActionButtons) {
      slotCompareCommands.clearSlots();
    }
    slotCompareCommands.stopComparing();
  };

  const onClearAll = () => {
    slotCompareCommands.clearSlots();
    slotCompareCommands.stopComparing();
  };

  const onAddAllToFavorites = () => {
    if (!userId || !casinoId) return;
    const toAdd = selectedSlots
      .filter((slot) => !isIncludedInFavorites(String(slot), favoritesList))
      .map(String);
    if (toAdd.length === 0) return;
    handleInsertFavoriteBulk(toAdd, userId, String(casinoId));
  };

  const onAddAllToPlaylist = () => {
    if (!userId || !casinoId) return;
    const toAdd = selectedSlots
      .filter((slot) => !isIncludedInPlaylist(slot, playlist))
      .map(String);
    if (toAdd.length === 0) return;
    handleInsertPlaylistBulk(toAdd, userId, String(casinoId));
  };


  return (
    <div className="comparison-header-v2">
      <div className="chv2-top-row">
        <button className="chv2-icon-btn" onClick={onBackAction} aria-label="Close compare">
          <IonIcon icon={close} />
        </button>

        <div className="chv2-title">
          <ReactSVG src="assets/icon/compare-icon.svg" className="chv2-compare-svg" />
          <span className="chv2-title-text">Comparing ({selectedSlots.length})</span>
        </div>

        <button className="chv2-icon-btn chv2-icon-btn--transparent" aria-label="View mode">
          <ReactSVG src="assets/icon/rotate-phone.svg" className="chv2-rotate-svg" />
        </button>
      </div>

      <div className="chv2-action-row">
        <button className="chv2-pill" onClick={onClearAll}>
          <IonIcon icon={checkmarkCircle} />
          <span>Clear ({selectedSlots.length})</span>
        </button>

        <button className="chv2-pill" onClick={onAddToFavorites ?? onAddAllToFavorites}>
          <IonIcon icon={heart} />
          <span>Add</span>
        </button>

        {isPlaylistEnabled && (
          <button className="chv2-pill" onClick={onAddToPlaylist ?? onAddAllToPlaylist}>
            <IonIcon icon={play} />
            <span>Add</span>
          </button>
        )}
      </div>

      <SortByPopup isOpen={showFilterPopover} onClose={() => setShowFilterPopover(false)} />
    </div>
  );
};

export default ComparisonViewHeader;
